import {useMemo} from "react";
import {clsx} from "clsx";
import {Button} from "@/primitives/Button";
import {FA6Icon} from "@/primitives/FA6Icon";
import {Modal} from "@/primitives/Modal";
import {ModalProvider} from "@/shared/reactIslandModal";
import {useDialogState} from "@/shared/useDialogState";
import type {PasswordStrengthValidation} from "@/features/auth/passwordStrengthValidation";
import {ChangePasswordModal} from "../changePasswordModal";

interface UserInformationPanelProps {
	clientIdentifier: string;
	accountOwner: string;
	showChangePasswordButton: boolean;
	hasConnectedMobileDevices: boolean;
	passwordStrengthValidation?: PasswordStrengthValidation;
}

export function UserInformationPanel({
	clientIdentifier,
	accountOwner,
	showChangePasswordButton,
	hasConnectedMobileDevices,
	passwordStrengthValidation,
}: UserInformationPanelProps) {
	const dialog = useDialogState();
	const modalContext = useMemo(() => ({close: dialog.close}), [dialog.close]);

	return (
		<div className="panel panel-flat">
			<div className="panel-heading">
				<h5 className="panel-title">{messages["user.settings.information.title"]}</h5>
			</div>
			<div className="panel-body form-horizontal">
				<InfoRow
					label={messages["user.settings.information.identifier"]}
					value={clientIdentifier}
				/>
				<InfoRow
					label={messages["user.settings.information.login"]}
					value={globals.userLogin}
				/>
				<InfoRow
					label={messages["user.settings.information.owner"]}
					value={accountOwner}
					last
				/>
				{showChangePasswordButton && (
					<div className="text-right">
						<Button onClick={() => dialog.show()}>
							<FA6Icon icon="key" variant="regular" fixedWidth className="mr-5"/>
							{messages["changePassword.change"]}
						</Button>
						<ModalProvider value={modalContext}>
							<Modal.Root open={dialog.open} onOpenChange={dialog.onOpenChange} disablePointerDismissal>
								{/* key remounts the form on every open, mirroring the ModalIsland behavior */}
								<ChangePasswordModal
									key={dialog.key}
									hasConnectedMobileDevices={hasConnectedMobileDevices}
									passwordStrengthValidation={passwordStrengthValidation}
								/>
							</Modal.Root>
						</ModalProvider>
					</div>
				)}
			</div>
		</div>
	);
}

function InfoRow({label, value, last = false}: {label: string; value: string; last?: boolean}) {
	return (
		<div className={clsx("form-group", last && "no-margin-bottom")}>
			<label className="col-lg-3 control-label text-right">{label}</label>
			<div className="col-lg-9">
				<div className="form-control no-border">{value}</div>
			</div>
		</div>
	);
}
